import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { HEIZUNGS_ARTEN } from "./enums";
import { ziffernFilter } from "./GrossZahlEingabe";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  head: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    cursor: "pointer",
  },
  checkbox: {
    accentColor: BRAND.purple,
    cursor: "pointer",
    width: "18px",
    height: "18px",
  },
  labelText: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  detail: {
    marginTop: "10px",
    paddingTop: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingBottom: "10px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  fieldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  fullSpan: {
    "@media (min-width: 640px)": {
      gridColumn: "1 / -1",
    },
  },
  radioRow: {
    display: "flex",
    columnGap: "16px",
  },
  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
});

export type HeizungSelections = Record<string, Record<string, string>>;

type Props = {
  value: HeizungSelections;
  onChange: (next: HeizungSelections) => void;
};

const HeizungsChecklist = ({ value, onChange }: Props) => {
  const styles = useStyles();
  const isChecked = (h: string) => value[h]?.selected === "true";
  const setField = (h: string, key: string, v: string) => {
    onChange({ ...value, [h]: { ...(value[h] ?? {}), [key]: v } });
  };
  const toggle = (h: string, next: boolean) => {
    setField(h, "selected", next ? "true" : "false");
  };

  return (
    <div className={styles.list}>
      {HEIZUNGS_ARTEN.map((h) => {
        const checked = isChecked(h);
        const entry = value[h] ?? {};
        const isBhkw = h === "Blockheizkraftwerk (BHKW)";
        const isPv = h === "Solar-/Photovoltaikanlage";
        return (
          <div key={h} className={styles.card}>
            <label className={styles.head}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={checked}
                onChange={(e) => toggle(h, e.target.checked)}
              />
              <span className={styles.labelText}>{h}</span>
            </label>
            {checked && isBhkw && (
              <div className={styles.detail}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Anzahl</span>
                  <input
                    className={styles.input}
                    value={entry.anzahl ?? ""}
                    onChange={(e) => setField(h, "anzahl", ziffernFilter(e.target.value))}
                    inputMode="numeric"
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Leistung (kW)</span>
                  <input
                    className={styles.input}
                    value={entry.leistungKw ?? ""}
                    onChange={(e) => setField(h, "leistungKw", ziffernFilter(e.target.value, { dezimal: true }))}
                    inputMode="numeric"
                  />
                </div>
              </div>
            )}
            {checked && isPv && (
              <div className={styles.detail}>
                <div className={mergeClasses(styles.field, styles.fullSpan)}>
                  <span className={styles.fieldLabel}>Art</span>
                  <div className={styles.radioRow}>
                    {["Solarthermie", "Photovoltaik"].map((opt) => (
                      <label key={opt} className={styles.radioOption}>
                        <input
                          type="radio"
                          checked={entry.art === opt}
                          onChange={() => setField(h, "art", opt)}
                          style={{ accentColor: BRAND.purple }}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Anzahl Anlagen</span>
                  <input
                    className={styles.input}
                    value={entry.anzahl ?? ""}
                    onChange={(e) => setField(h, "anzahl", ziffernFilter(e.target.value))}
                    inputMode="numeric"
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Leistung (kWp)</span>
                  <input
                    className={styles.input}
                    value={entry.leistungKwp ?? ""}
                    onChange={(e) => setField(h, "leistungKwp", ziffernFilter(e.target.value, { dezimal: true }))}
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Baujahr</span>
                  <input
                    className={styles.input}
                    value={entry.baujahr ?? ""}
                    onChange={(e) => setField(h, "baujahr", ziffernFilter(e.target.value, { maxLen: 4 }))}
                    inputMode="numeric"
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Hersteller</span>
                  <input
                    className={styles.input}
                    value={entry.hersteller ?? ""}
                    onChange={(e) => setField(h, "hersteller", e.target.value)}
                  />
                </div>
                <div className={mergeClasses(styles.field, styles.fullSpan)}>
                  <span className={styles.fieldLabel}>Errichter / Installationsfirma</span>
                  <input
                    className={styles.input}
                    value={entry.errichter ?? ""}
                    onChange={(e) => setField(h, "errichter", e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Gesamtkosten inkl. Installation (€)</span>
                  <input
                    className={styles.input}
                    value={entry.kosten ?? ""}
                    onChange={(e) => setField(h, "kosten", ziffernFilter(e.target.value, { dezimal: true }))}
                  />
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Montageort</span>
                  <input
                    className={styles.input}
                    value={entry.montageort ?? ""}
                    onChange={(e) => setField(h, "montageort", e.target.value)}
                  />
                </div>
                <div className={mergeClasses(styles.field, styles.fullSpan)}>
                  <span className={styles.fieldLabel}>Sachverständigenabnahme vorhanden?</span>
                  <div className={styles.radioRow}>
                    {["ja", "nein"].map((opt) => (
                      <label key={opt} className={styles.radioOption}>
                        <input
                          type="radio"
                          checked={entry.svAbnahme === opt}
                          onChange={() => setField(h, "svAbnahme", opt)}
                          style={{ accentColor: BRAND.purple }}
                        />
                        {opt === "ja" ? "Ja" : "Nein"}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HeizungsChecklist;
